import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import moment from 'moment-timezone';
import { sprintf } from 'sprintf-js';
import QueryString from 'query-string';

import { GraphDataConverter } from '../../helpers';
import { GroupingsService, AssetsService, DeviceDataOverrideService } from '../../services';
import PageTitle from '../PageTitle/page_title';
import { DatePickerInput } from '../DatePickerInput';
import i18next from "i18next";
import {LineGraph, DoorAreaGraph, StateAreaGraph} from '../Graphs';

import config from "config";
import Alert from 'react-bootstrap/lib/Alert';

const AllAssetsReport = ({location}) => {
    const passedState = window.state;
    const urlSearch = passedState ? QueryString.parse(location.search) : [];
    const fromPrint = urlSearch["print"] === "true";

    const [reportConfiguration, setReportConfiguration] = useState({
        groupingId: null,
        dateRange: fromPrint ? passedState.dateRange : null,
        startDate: fromPrint ? passedState.startDate : "",
        selectedGrouping: fromPrint ? passedState.selectedGrouping : null,
        includeDevices: true,
        firstPrimaryOnly: fromPrint ? passedState.firstPrimaryOnly : false,
        limitSampleRate: fromPrint ? passedState.limitSampleRate : false,
        viewAverages: fromPrint ? passedState.viewAverages : false,
        fromPrint: fromPrint,
    });
    const [availableGroupingValues, setAvailableGroupingValues] = useState(fromPrint ? passedState.availableGroupingValues : null);
    const [allDeviceReport, setAllDeviceReport] = useState(fromPrint ? passedState.allDeviceReport : "");
    const [areaGraphData, setAreaGraphData] = useState(fromPrint ? passedState.areaGraphData : "");
    const [printing, setPrinting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [assetsEnabled, setAssetsEnabled] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const generateChartVitals = (data, limitSampleRate, dateRange) => {
      if (data && data.readings && data.readings.length) {
          const finalDisplayData = GraphDataConverter.convertReadingDataToGraphData(
            data.deviceDataTypeToken,
            data.timezone,
            data.readings,
            data.endTimestamp,
            Math.round((data.endTimestamp - data.startTimestamp) / 1000),
            limitSampleRate,
            3600000 // 60 minutes
        );

        if (finalDisplayData) {
          const outagesAtStartOrEnd = GraphDataConverter.convertOutagesToGraphData(data.outagesAtStartOrEnd, data.timezone);

          var averageValue = null;
          if (data.valueChange && data.pctChange && data.currAverage && data.prevAverage) {
            const displayFormat = i18next.t([`device-data-display-types.format.${data.deviceDataTypeToken}`, "device-data-display-types.format.numeric"]);
            const currentAverage = sprintf(displayFormat, data.currAverage);
            const previousAverage = sprintf(displayFormat, data.prevAverage);
            const valueChange = `${data.valueChange > 0 ? "+" : ""}${sprintf(displayFormat, data.valueChange)}`;
            const percentChange = `${data.valueChange > 0 ? "+" : "-"}${sprintf("%0.2f", data.pctChange)}%`;
            averageValue = `${i18next.t("abbr-average")}: ${currentAverage}, ${i18next.t("abbr-previous-average")}: ${previousAverage}, ${i18next.t("change")}: ${valueChange} (${percentChange})`;        
          }

          const label = data.deviceDataTypeToken == 'online' ? null : data.deviceDataTypeDisplayName ?? i18next.t([`device-data-display-types.${DeviceDataOverrideService.getOverrideForDisplayToken(data.deviceDataTypeToken)}`, `device-data-types.${data.deviceDataTypeToken}`]);
          if (data.deviceDataTypeToken === 'magnetometer' || data.deviceDataTypeToken === 'door_open') {
            return { 
              type: "door", 
              data: finalDisplayData, 
              timeframe: dateRange, 
              outagesAtStartOrEnd: outagesAtStartOrEnd,
              label: label,
              averageValue: averageValue
            };
          } else if (GraphDataConverter.hasStateValue(finalDisplayData)) {
            return { 
              type: data.deviceDataTypeToken, 
              data: finalDisplayData, 
              timeframe: dateRange, 
              outagesAtStartOrEnd: outagesAtStartOrEnd,
              label: label,
              averageValue: averageValue
            };
          } else {
            return { 
              type: "line", 
              data: finalDisplayData,               
              outagesAtStartOrEnd: outagesAtStartOrEnd,
              label: label,
              averageValue: averageValue
            };
          }
        }
      }

      return null;
    };

    const populateChartVitalsByArea = (areaData, firstPrimaryOnly, limitSampleRate, dateRange, areaGraphData) => {
      areaData.forEach((areaDeviceOrAsset) => {
        if (areaGraphData[areaDeviceOrAsset.areaName] == null) {
          areaGraphData[areaDeviceOrAsset.areaName] = {
            areaName: areaDeviceOrAsset.areaName, 
            entityData: [],
            entityType: areaDeviceOrAsset.deviceData ? "device" : "asset"
          };
        }

        const areaMeasurements = areaDeviceOrAsset.deviceData ?? areaDeviceOrAsset.assetData;

        areaMeasurements.forEach((measurements) => {
          var deviceName = null;
          var currAverage = 0.0;

          let chartsByDataType = [];

          Object.values(measurements).forEach((measurementEntry) => { // For each measurement of the device or asset                          
            if (deviceName == null) {
              deviceName = measurementEntry.deviceName;
              currAverage = measurementEntry.currAverage;

              const chartVitals = generateChartVitals(measurementEntry, limitSampleRate, dateRange);
              if (chartVitals) {
                  chartsByDataType.push(chartVitals);
              }
            } else if (!firstPrimaryOnly) {
              const chartVitals = generateChartVitals(measurementEntry, limitSampleRate, dateRange);
              if (chartVitals) {
                  chartsByDataType.push(chartVitals);
              }
            }
          });

          areaGraphData[areaDeviceOrAsset.areaName].entityData.push({entityName: deviceName, currAverage: currAverage, graphData: chartsByDataType});
        });
      });
    }    
    
    const reportWindow = (reportConfiguration) => {
      const start = moment(reportConfiguration.startDate).startOf("day");
      var end;
      if (reportConfiguration.dateRange.value === 0) { // day report
        end = moment(start).add(1, 'day')
      } else if (reportConfiguration.dateRange.value === 1) { // week report
        end = moment(start).add(1, 'week')
      } else if (reportConfiguration.dateRange.value === 2) { // month report
        end = moment(start).add(1, 'month')
      }

      return { start, end };
    };

    const fetchReport = (reportConfiguration, complete) => {
      const { start, end } = reportWindow(reportConfiguration);

      AssetsService.getAllAssetReport(start.valueOf(), end.valueOf(), reportConfiguration.groupingId, !reportConfiguration.includeDevices).then(response => {
        let allDeviceReport = response.allDeviceReport;

        // Prune out any empty devices or empty areas
        allDeviceReport.devicesDataByArea = allDeviceReport.devicesDataByArea.map(areaDeviceReport => {
          areaDeviceReport.deviceData = areaDeviceReport.deviceData.filter(deviceData => Object.values(deviceData).length !== 0);
          return areaDeviceReport;
        }).filter(areaDeviceReport => areaDeviceReport.deviceData.length !== 0);

        allDeviceReport.assetsDataByArea = allDeviceReport.assetsDataByArea.map(areaAssetReport => {
          areaAssetReport.assetData = areaAssetReport.assetData.filter((assetData) => Object.values(assetData).length !== 0);
          return areaAssetReport;
        }).filter(areaAssetReport => areaAssetReport.assetData.length !== 0);
        
        const areaGraphData = {};
        if (reportConfiguration.includeDevices || (allDeviceReport.warningMessage && allDeviceReport.devicesDataByArea.length > 0)) {
          const fpo = reportConfiguration.firstPrimaryOnly || (allDeviceReport.warningMessage && allDeviceReport.devicesDataByArea.length > 0);
          populateChartVitalsByArea(allDeviceReport.devicesDataByArea, fpo, reportConfiguration.limitSampleRate, reportConfiguration.dateRange, areaGraphData);
        }
        populateChartVitalsByArea(allDeviceReport.assetsDataByArea, reportConfiguration.firstPrimaryOnly, reportConfiguration.limitSampleRate, reportConfiguration.dateRange, areaGraphData);
        complete(allDeviceReport, Object.values(areaGraphData));
      }).catch(error => {
        console.error(error);
        // TODO deal with error message
        complete(null, null);
      });
    };

    // The export sends the same window and the same include-devices state the report on screen was run with,
    // so the file matches what the user is looking at.
    const exportClicked = (reportConfiguration) => {
      const { start, end } = reportWindow(reportConfiguration);

      setExportLoading(true);

      AssetsService.getAllAssetReportCSV(start.valueOf(), end.valueOf(), reportConfiguration.groupingId, !reportConfiguration.includeDevices)
        .then(() => setExportLoading(false))
        .catch((error) => {
          console.error(error);
          setExportLoading(false);
        });
    };

    const printClicked = (reportConfiguration, allDeviceReport, availableGroupingValues, areaGraphData) => {
      let printWindow = window.open(`${window.location.href}?print=true`, '_blank') //to open new page
      printWindow.state = {
        dateRange: reportConfiguration.dateRange,
        selectedGrouping: reportConfiguration.selectedGrouping,
        startDate: moment(reportConfiguration.startDate).format('L'),
        allDeviceReport: allDeviceReport,
        availableGroupingValues: availableGroupingValues,
        areaGraphData: areaGraphData,
        firstPrimaryOnly:reportConfiguration.firstPrimaryOnly,
        limitSampleRate: reportConfiguration.limitSampleRate,
        viewAverages: reportConfiguration.viewAverages
      };
    };

    useEffect(() => {
      if (reportConfiguration.fromPrint && !printing) {
        setTimeout(() => {
          window.print();
          setPrinting(true);
        }, 500);
      } else if (!availableGroupingValues) {
        setLoading(true);

        let requests = [];

        requests.push(AssetsService.getAll().then((response) => {
          const enabled = response.assets && response.assets.length > 0;
          setAssetsEnabled(enabled);
          setReportConfiguration({...reportConfiguration, includeDevices: !enabled});
        }));

        requests.push(GroupingsService.listAll().then((response) => {
          const leafGroupings = response.groupings
            .filter((grouping) => grouping.deviceGroup?.children?.length == 0)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((grouping) => ({ value: grouping.id, label: grouping.name, grouping: grouping }));

          setAvailableGroupingValues(leafGroupings);
        }));

        Promise.all(requests).then(() => {
          setLoading(false);
        }).catch((error) => {
          console.error(error);
          // this.setState({ errorMessage: i18next.t('messages.could-not-load-groupings') });
          // TODO deal with error message
          setLoading(false);
        });
      }
    }, [reportConfiguration, printing]);

    if (availableGroupingValues) {
      return (
          <div className = "device-detail-report">
            <div className="row">
              <PageTitle title={ i18next.t('pages.all-assets-report.header') } />
            </div>
            <div className="op-content-box filter-box" style={{marginBottom: 20}}>
                <ul className="list-inline clearfix">
                  <li className="pull-left box-label"><h4 className="remove-margin-bottom">{ i18next.t('filters') }</h4></li>
                </ul>

                <ReportConfigurationArea 
                  reportConfiguration={reportConfiguration}
                  availableGroupingValues={availableGroupingValues}
                  printClicked={() => printClicked(reportConfiguration, allDeviceReport, availableGroupingValues, areaGraphData)}
                  exportClicked={() => exportClicked(reportConfiguration)}
                  exportLoading={exportLoading}
                  fullyLoaded={allDeviceReport && areaGraphData}
                  assetsEnabled={assetsEnabled}
                  updateReportConfiguration={(reportConfiguration) => {
                    if (reportConfiguration.dateRange && reportConfiguration.selectedGrouping && reportConfiguration.startDate) {
                      setLoading(true);
                      setReportConfiguration(reportConfiguration);
                      setAreaGraphData(null);
                      fetchReport(reportConfiguration, (allDeviceReport, areaGraphData) => {
                        setAllDeviceReport(allDeviceReport);
                        setAreaGraphData(areaGraphData);
                        setLoading(false);
                      });
                    }
                  }}
                />
            </div>

            {
              allDeviceReport?.warningMessage && <Alert bsStyle="warning">{allDeviceReport?.warningMessage}</Alert>
            }

            {
              ((allDeviceReport && areaGraphData) || loading) &&
                <Reports 
                  areaGraphData={areaGraphData} 
                  loading={loading}
                  viewAverages={reportConfiguration.viewAverages} 
                  firstPrimaryOnly={reportConfiguration.firstPrimaryOnly}
                />
            }

          </div>
      );
    } else {
      return (
          <div className="loader">{ i18next.t('loading') }</div>
      );
    }
};

const ReportConfigurationArea = ({reportConfiguration, availableGroupingValues, printClicked, exportClicked, exportLoading, updateReportConfiguration, fullyLoaded, assetsEnabled}) => {
  const [includeDevices, setIncludeDevices] = useState(reportConfiguration.includeDevices);
  const [firstPrimaryOnly, setFirstPrimaryOnly] = useState(reportConfiguration.firstPrimaryOnly);
  const [limitSampleRate, setLimitSampleRate] = useState(reportConfiguration.limitSampleRate);
  const [viewAverages, setViewAverages] = useState(reportConfiguration.viewAverages);
  const [dateRange, setDateRange] = useState(reportConfiguration.dateRange);
  const [startDate, setStartDate] = useState(reportConfiguration.startDate);
  const [selectedGrouping, setSelectedGrouping] = useState(reportConfiguration.selectedGrouping);
  const [readyToRun, setReadyToRun] = useState(false);

  useMemo(() => {
    setReadyToRun(dateRange != null && startDate != null && startDate != '' && selectedGrouping != null);
  }, [dateRange, startDate, selectedGrouping]);

  useMemo(() => {
    setIncludeDevices(reportConfiguration.includeDevices);
  }, [reportConfiguration]);

  return (
      <div className="row">
          <div className="col-md-3 col-sm-3">
              <label>{i18next.t("form-labels.timeframe")}</label>
              <Select
                  options={[
                      { value: 0, label: i18next.t("form-labels.day") },
                      { value: 1, label: i18next.t("form-labels.week") },
                      { value: 2, label: i18next.t("form-labels.month") },
                  ]}
                  clearable={false}
                  value={dateRange}
                  onChange={setDateRange}
                  searchable={false}
                  classNamePrefix="react-select"
                  placeholder={i18next.t("form-labels.select-timeframe")}
                  isDisabled={reportConfiguration.fromPrint}
              />
          </div>

          <div className="col-md-2 col-sm-3">
              <label>{i18next.t("form-labels.start-date")}</label>
              <DatePickerInput input={{ value: startDate, isDisabled: reportConfiguration.fromPrint }} meta={{}} onDayChange={setStartDate} />
          </div>

          <div className="col-md-4 col-sm-3">
              <label>{i18next.t("form-labels.location")}</label>
              <Select
                  options={availableGroupingValues}
                  clearable={false}
                  value={selectedGrouping}
                  onChange={setSelectedGrouping}
                  searchable={false}
                  classNamePrefix="react-select"
                  placeholder={i18next.t("form-labels.select-location")}
                  isDisabled={reportConfiguration.fromPrint}
              />
          </div>

          <div className="col-md-2 col-sm-3" style={{ marginTop: "10px" }}>
              {assetsEnabled && (
                  <label>
                      <input type="checkbox" className="inline" checked={includeDevices} onChange={() => setIncludeDevices(!includeDevices)} />
                      Include Devices
                  </label>
              )}
              <label>
                  <input type="checkbox" className="inline" checked={firstPrimaryOnly} onChange={() => setFirstPrimaryOnly(!firstPrimaryOnly)} />
                  {i18next.t("form-labels.primary-reading-only")}
              </label>
              <label>
                  <input type="checkbox" className="inline" checked={limitSampleRate} onChange={() => setLimitSampleRate(!limitSampleRate)} />
                  {i18next.t("form-labels.sixty-min-frequency")}
              </label>
              <label>
                  <input type="checkbox" className="inline" checked={viewAverages} onChange={() => setViewAverages(!viewAverages)} />
                  {i18next.t("form-labels.view-averages")}
              </label>
          </div>

          {!reportConfiguration.fromPrint && fullyLoaded && (
              <div className="pull-right left-print tooltip">
                  <a>
                      <img src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/print_icon.svg`} className="print-icon" onClick={printClicked} alt={i18next.t("print-report")} />
                  </a>
                  <span className="tooltiptext left-print">{i18next.t("print")}</span>
              </div>
          )}

          {!reportConfiguration.fromPrint && fullyLoaded && (
              <div className="pull-right left-print tooltip">
                  <a>
                      <img src={`${config.WEB_BASE_URL}/images/icon/themes/${window.globalBrand}/export_icon.svg`} id="export-icon" className={exportLoading ? "print-icon spinner" : "print-icon"} onClick={exportClicked} alt={i18next.t("table-headers.export-data")} />
                  </a>
                  <span className="tooltiptext left-print">{i18next.t("table-headers.export-data")}</span>
              </div>
          )}

          <div className="col-md-1 col-sm-1">
          {!reportConfiguration.fromPrint && readyToRun && (
              <div className="pull-left">
                  <label style={{ visibility: "hidden" }}>{i18next.t("form-labels.run")}</label>
                  <ul className="list-inline clearfix">
                      <li className="pull-left" style={{ marginLeft: "15px" }}>
                          <button
                              type="submit"
                              onClick={() =>
                                  updateReportConfiguration({
                                      dateRange: dateRange,
                                      startDate: startDate,
                                      selectedGrouping: selectedGrouping,
                                      groupingId: selectedGrouping.value,
                                      includeDevices: includeDevices,
                                      firstPrimaryOnly: firstPrimaryOnly,
                                      limitSampleRate: limitSampleRate,
                                      viewAverages: viewAverages,
                                      fromPrint: reportConfiguration.fromPrint,
                                  })
                              }
                              className="btn btn-sm btn-default pull-right"
                          >
                              {i18next.t("form-labels.run")}
                          </button>
                      </li>
                  </ul>
              </div>
          )}
          </div>
      </div>
  );
};

const Reports = ({areaGraphData, loading, viewAverages, firstPrimaryOnly}) => loading ? (<div className="loader">Loading...</div>) : (
    <div>
      <div>
        <div className="op-content-box" style={{marginLeft:"30px"}}>
          <div className="l-region__heading">
            <h2 style={{fontFamily: 'roboto'}}> { i18next.t('pages.all-assets-report.readings-over-time') } </h2>
          </div>

          {
            areaGraphData.map((areaReport, areaIndex) => 
              <div key={areaIndex} style={{paddingBottom:20}}>
                <h4 style={{fontFamily: 'roboto', paddingBottom:"5px", borderBottom: "1px solid #0000002b"}}> <b>{areaReport.areaName.toUpperCase()}</b> </h4>
                {
                  areaReport.entityData.map((entity, deviceIndex) => 
                    <ReportDetailArea 
                      entityName={entity.entityName} 
                      entityIndex={deviceIndex}
                      graphData={entity.graphData}
                      viewAverages={viewAverages}
                      firstPrimaryOnly={firstPrimaryOnly}
                    />
                  )
                }
              </div>
            )
          }
        </div>
      </div>
    </div>
);

const ReportDetailArea = ({entityName, entityIndex, graphData, viewAverages, firstPrimaryOnly}) => {
  const [entityCharts, setEntityCharts] = useState([]);
  const [chartHeading, setChartHeading] = useState(entityName);
  
  useMemo(() => {
    setEntityCharts(
      graphData.map((chartVitals, dataTypeIndex) => {
        var graphTag = <LineGraph graphVitals={chartVitals} showBrush={false} width={860} height={350} id={entityIndex} />;
        if (chartVitals.type === "door") {
          graphTag = <DoorAreaGraph graphVitals={chartVitals} showBrush={false} width={860} height={350} id={entityIndex} />;
        } else if (GraphDataConverter.hasStateValue(chartVitals.data)) {
          graphTag = <StateAreaGraph graphVitals={chartVitals} dataTypeToken={chartVitals.type} showBrush={false} width={860} height={350} id={entityIndex} />;
        }
        return (<div key={dataTypeIndex}>{graphTag}</div>);
      })
    );
  }, [graphData, entityName])

  useMemo(() => {
    if (viewAverages) {
      var averages = [];

      if (firstPrimaryOnly) {
        const firstPrimaryData = Object.values(graphData)[0];
        averages = firstPrimaryData?.averageValue != null ? [firstPrimaryData.averageValue] : [];
      } else {
        averages = Object.values(graphData).filter(data => data.averageValue != null).map(data => data.averageValue);
      }

      setChartHeading(averages.length ? <span>{entityName} [{averages.join(', ')}]</span> : <span>{entityName}</span>);
    } else {
      setChartHeading(entityName);
    }
  }, [entityName, viewAverages, firstPrimaryOnly]);

  return (
    <div key={entityIndex} style={entityCharts.length === 0 ? {paddingBottom:10} : null}>
      <b>{chartHeading}</b> <br/>
      {entityCharts.length > 0 ? <span>{entityCharts}</span> : <i style={{paddingTop:10}}>{ i18next.t('messages.no-data-to-graph') }</i>}
      <br/>
    </div>
  );
}

export default AllAssetsReport;
